<script setup>
  import { useOfferStore } from '../../stores/OfferStore';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { showPriceWithCurrency } from '../../utils/format_offer'
  import ConfirmValidate from '../../pages/cart/ConfirmValidate.vue';
  import Modal from '../ui/Modal.vue';
  import { useConfirmValidateStore } from '../../stores/ConfirmValidateStore';

  const emits = defineEmits(['deleteReplacedOffers']);

  const { t } = useI18n();
  const offerStore = useOfferStore();
  const cartStore = useBrowseCartStore();
  const validateStore = useConfirmValidateStore();
  const loading = computed(() => {
    return offerStore.loading;
  });

  const formatted_cart_budget = computed(() => {
    // Check values before continuing, make sure no zero or null budget
    if (!cartStore.cart || !cartStore.cart.budget_amount || cartStore.cart.budget_amount <= 0) {
      return null;
    }
    let budget = cartStore.cart.budget_amount;
    let currency = cartStore.cart.budget_currency || cartStore.cart.financial.billing_currency;
    return showPriceWithCurrency(currency, budget);
  });

  const cart_budget_percent = computed(() => {
    if (cartStore.cart && cartStore.cart.budget_amount && cartStore.cart.budget_amount > 0) {
      let budget = cartStore.cart.budget_amount;
      let total_price = totalWithTaxes.value;
      return total_price / budget;
    }
  });

  const cart_budget_class = computed(() => {
    if (cart_budget_percent.value > 1) {
      return 'text-danger';
    } else if (cart_budget_percent.value > 0.9) {
      return 'text-warning';
    } else {
      return 'text-success';
    }
  });

  const cart_budget_icon = computed(() => {
    if (cart_budget_percent.value > 1) {
      return 'bi-x-circle-fill';
    } else if (cart_budget_percent.value > 0.9) {
      return 'bi-exclamation-circle-fill';
    } else {
      return 'bi-check-circle-fill';
    }
  });

  const cart_budget_details = computed(() => {
    let remaining_amount = cartStore.cart.budget_amount - totalWithTaxes.value;
    let currency = cartStore.cart.budget_currency || cartStore.cart.financial.billing_currency;

    let formatted_remaining_amount = showPriceWithCurrency(currency, Math.abs(remaining_amount));
    let remaining_amount_percent = (Math.abs(1 - cart_budget_percent.value) * 100).toFixed(0);
    let text = cart_budget_percent.value < 1 ? t('cart.under_budget') : t('cart.over_budget');

    return `${formatted_remaining_amount} (${remaining_amount_percent}%) ${text}`;
  });

  const policy = computed(() => {
    return cartStore.cart.policy;
  });

  const deleteReplacedOffers = () => {
    // Alert the user that they have replaced offers that are non-validated
    if (offerStore.offersSelectedReplaced.length > 0) {
      if (!confirm(t('cart.replace_offers_warning'))) {
        return;
      }
    }
    emits('deleteReplacedOffers');
    showModal.value = true;
  }

  const cancelValidation = async () => {
    await validateStore.cancelValidation(cartStore.cart.id);
    if(!validateStore.errors) {
      cartStore.cart.status = validateStore.success.status;
      cartStore.cart.will_validate_at = null;
      cartStore.cart.customer_service = validateStore.success.customer_service;
      cartStore.cart.policy = validateStore.success.policy;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const showModal = ref(false);

  const allPrices = computed(() => {
    return cartStore.allComputedPrices;
  });

  const totalWithTaxes = computed(() => {
    return allPrices.value.totalWithTaxes;
  });

  const billingCurrency = computed(() => {
    return allPrices.value.currency;
  });
</script>

<template>
  <div class="sticky--bottom full-screen" v-if="!loading">
    <Modal :show="showModal" @close="showModal = false" size="medium">
      <template v-slot:title>
        {{ t('cart.confirm_validation.title') }}
      </template>
      <ConfirmValidate :cartId="cartStore.cart.id" @closeModal="showModal = false" />
    </Modal>

    <div class="cart-sticky-container">
      <div class="row justify-content-end ms-5 me-5 align-items-center">
        <div class="col">
          <button v-if="policy.cart_can_be_validated && policy.cart_validate && !policy.impersonate"
            role="button"
            class="btn btn-primary"
            @click="deleteReplacedOffers()">
            {{ t('cart.cart_validate') }}
          </button>
          <button v-if="policy.cart_validate && cartStore.cart.status == 'to_process'"
            role="button"
            class="btn btn-danger"
            @click="cancelValidation()">
            {{ t('cart.cart_cancel_validation') }}
          </button>
        </div>
        <div class="col-auto">
          <div class="row">
            <div class="col">
              <span class="cart-total-ttc-label">{{ t('cart.total_with_taxes') }} :</span>
              <span class="cart-total-ttc-total">{{ showPriceWithCurrency(billingCurrency, totalWithTaxes) }}</span>
              <span class="cart-total-ttc-budget" v-if="formatted_cart_budget"> / <span :class="cart_budget_class">{{ formatted_cart_budget }} <i class="bi" :class="cart_budget_icon" data-bs-toggle="tooltip" :data-bs-title="cart_budget_details" :aria-label="cart_budget_details"></i></span></span>
            </div>
          </div>
          <div class="row" v-if="formatted_cart_budget">
            <div class="col">
              <span :class="cart_budget_class">{{ cart_budget_details }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
